import { useEffect, useState, useCallback } from 'react';
import BluebirdPromise from 'bluebird';
import { pluginRunner } from 'app/scripts/views/internal/plugins/PluginRunner';
import { Board, Plugin, PluginBoardButton } from './PluginHeaderButton.types';
import { useDebouncedCallback } from 'use-debounce';
import { BoardPlugin } from 'app/scripts/models/BoardPlugin';
import { BUTLER_POWER_UP_ID } from 'app/scripts/data/butler-id';
import {
  mapPowerUpId as MAP_POWER_UP_ID,
  customFieldsId as CUSTOM_FIELDS_POWER_UP_ID,
} from '@trello/config';
import isEqual from 'react-fast-compare';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const omitCallback = <T extends { callback?: any }>({
  callback,
  ...rest
}: T): Omit<T, 'callback'> => rest;

export const usePluginData = (
  board: Board,
  command?: string,
  hiddenPluginBoardButtons?: string[],
) => {
  const [plugins, setPlugins] = useState<Plugin[] | undefined>([]);
  const [pluginCommandData, setPluginCommandData] = useState<
    object[] | undefined
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  const filterCorePlugins = useCallback(
    (pluginObj: BoardPlugin | Plugin | PluginBoardButton) => {
      if (!pluginObj) {
        console.warn('Problem loading board button');
        return false;
      }
      if (board.isButlerCore()) {
        if (pluginObj instanceof BoardPlugin && pluginObj.isButler()) {
          return false;
        } else if ('id' in pluginObj && pluginObj.id === BUTLER_POWER_UP_ID) {
          return false;
        } else if (
          'idPlugin' in pluginObj &&
          pluginObj.idPlugin === BUTLER_POWER_UP_ID
        ) {
          return false;
        }
      }
      if (board.isMapCore()) {
        if (pluginObj instanceof BoardPlugin && pluginObj.isMap()) {
          return false;
        } else if ('id' in pluginObj && pluginObj.id === MAP_POWER_UP_ID) {
          return false;
        } else if (
          'idPlugin' in pluginObj &&
          pluginObj.idPlugin === MAP_POWER_UP_ID
        ) {
          return false;
        }
      }
      if (board.isCustomFieldsCore()) {
        if (pluginObj instanceof BoardPlugin && pluginObj.isCustomFields()) {
          return false;
        } else if (
          'id' in pluginObj &&
          pluginObj.id === CUSTOM_FIELDS_POWER_UP_ID
        ) {
          return false;
        } else if (
          'idPlugin' in pluginObj &&
          pluginObj.idPlugin === CUSTOM_FIELDS_POWER_UP_ID
        ) {
          return false;
        }
      }
      if (hiddenPluginBoardButtons) {
        if (
          'id' in pluginObj &&
          hiddenPluginBoardButtons.includes(pluginObj.id)
        ) {
          return false;
        } else if (
          'idPlugin' in pluginObj &&
          hiddenPluginBoardButtons.includes(pluginObj.idPlugin)
        ) {
          return false;
        }
      }
      return true;
    },
    [board, hiddenPluginBoardButtons],
  );

  const boardPlugins = board.boardPluginList?.models?.filter(filterCorePlugins);

  const loadPluginDataCallback = useCallback(() => {
    const PluginsPromise = BluebirdPromise.map(
      boardPlugins,
      (boardPlugin: { get: (arg0: string) => unknown }) => {
        return pluginRunner.getOrLoadPlugin(board, boardPlugin.get('idPlugin'));
      },
    );

    if (command) {
      const runnerOptions = {
        command: command,
        timeout: 10000,
        board: board,
      };

      const PluginCommandPromise = pluginRunner.all(runnerOptions);
      const promises = BluebirdPromise.all([
        PluginsPromise,
        PluginCommandPromise,
      ]);

      promises
        .then(([pluginsData, pluginCommandPromiseData]) => {
          pluginCommandPromiseData = pluginCommandPromiseData.filter(
            filterCorePlugins,
          );
          pluginsData = pluginsData.filter(filterCorePlugins);

          if (pluginsData.length !== plugins?.length) {
            setPlugins(pluginsData);
          }

          if (pluginCommandPromiseData.length !== pluginCommandData?.length) {
            setPluginCommandData(pluginCommandPromiseData);
          }
          //Checking the length is not enough in the case a power-up that returns the same amount of buttons with different behavior
          else if (pluginCommandData && pluginCommandPromiseData) {
            if (
              pluginCommandPromiseData.some(
                (x, index) =>
                  !isEqual(
                    omitCallback(x),
                    omitCallback(pluginCommandData[index]),
                  ),
              )
            ) {
              setPluginCommandData(pluginCommandPromiseData);
            }
          }
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          console.warn(err);
        });

      return () => {
        promises.cancel();
      };
    } else {
      PluginsPromise.then((pluginsData) => {
        pluginsData = pluginsData.filter(filterCorePlugins);

        if (pluginsData.length !== plugins?.length) {
          setPlugins(pluginsData);
        }
        setIsLoading(false);
      }).catch((err) => console.warn(err));
      return () => PluginsPromise.cancel();
    }
  }, [
    boardPlugins,
    command,
    board,
    filterCorePlugins,
    pluginCommandData,
    plugins?.length,
    setPluginCommandData,
  ]);

  //Creating call as a callback behind a debounce as certain actions can cause lots of re-renders
  //Such as mass archiving and we don't want to call the pluginRunner excessively
  const debouncedLoadPluginDataCallback = useDebouncedCallback(
    loadPluginDataCallback,
    500,
  );

  useEffect(() => {
    if (!boardPlugins) {
      setPlugins(undefined);
      setPluginCommandData(undefined);
      setIsLoading(false);
      return;
    }
    debouncedLoadPluginDataCallback();
  }, [boardPlugins, debouncedLoadPluginDataCallback]);

  if (!plugins) {
    return {
      isLoading: isLoading,
      pluginsData: undefined,
      error: 'board plugins are undefined',
    };
  }

  return {
    isLoading: isLoading,
    pluginsData: {
      plugins: plugins,
      commandData: pluginCommandData,
    },
    error: '',
  };
};
